// ═══════════════════════════════════════════════════════════════════
//  TOQUM · SUPPLIER MODULE
//  Registration follows Nirvikalp Sourcing.js in full (identity,
//  contacts, management, media, type-specific capacity, evaluation,
//  capacity calendar) plus Toqum's quality-specific blocks.
//  Audit & CAP live in SupplierAudit.js.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, Page, CC, uid, today, addDays, num, Section, Field, Grid, Btn, DataTable, TabBar, Tag, Badge,
  StatCard, RatingStars, DynamicTable, MapEmbed, MiniBar, Collapse, Modal, Toast, printHTML,
  MONTHS_SHORT, MONTH_DAYS, SUNDAYS, isSuperAdmin, isManager, hasRight,
} from './Shared';
import { AuditTab, CapTab } from './SupplierAudit';

// ── Supplier shape — Sourcing INIT_SUP + Toqum quality fields ─────
export const INIT_SUP = {
  id:null, name:'', companyId:'', supplierType:'Garment Supplier',
  category:'Garment',
  address:        { city:'', state:'', country:'', zip:'', lat:'', lng:'' },
  factoryAddress: { city:'', state:'', country:'', zip:'', lat:'', lng:'' },
  region:'', taxId:'', ownership:'Sole Proprietorship',
  owners:[], customers:[], factories:[], productCats:[],
  contact:{ person:'', phone:'', mobile:'', email:'', website:'', whatsapp:'', linkedin:'', instagram:'' },
  qaContacts:[],                                  // factory-side quality contacts
  images:{ companyLogo:'', companyImages:[], factoryImages:[] },
  files:{ profile:'', gallery:'' },
  // Garment capacity
  totalSewingArea:'', totalLines:0, sewingLines:[],
  cutting:{ area:'', tables:'', length:'', cutters:'', spreaders:'', type:'Manual', brand:'' },
  finishing:{ area:'', tables:'', packers:'', capacity:'' },
  // Raw material
  rmType:'Fabric',
  // Value addition
  vaType:'Print',
  vaDetails:{ technique:'', mode:'Manual', sampleCap:'', prodCap:'', leadTime:'', tablesOrMachines:'',
              handworkers:'', umbrellaMachines:'', industrialMachines:'', washingMachines:'',
              dryingMachines:'', ppStations:'', laserMachines:'' },
  // Evaluation
  evaluation:{ sustScore:0, waterUsage:'', ghgEmission:'', wasteMgmt:'Basic', socialAudit:'',
               certifications:[], qualityScore:0, rejectionRate:'', lastAuditScore:'', auditDate:'', qualityCerts:[] },
  // Quality infrastructure (Toqum addition — drives audit readiness)
  qualitySetup:{ qcHeadcount:'', inlineQcPerLine:'', endlineQc:'', qaIndependent:'No', labOnSite:'No',
                 metalDetector:'No', pullTester:'No', needlePolicy:'No', aqlStandard:'2.5',
                 fourPointSystem:'No', trafficLight:'No', measurementAuditFreq:'' },
  calendar: Array(12).fill(null).map((_,i)=>({ month:i+1, holidays:0, workingDays: MONTH_DAYS[i]-SUNDAYS[i] })),
  agreedWastage:'', paymentTerms:'', leadTime:'',
  status:'Pending Approval', approvalStatus:'Pending Approval', createdOn:'',
};

const SUST_CERTS = ['GOTS','OEKO-TEX','GRS','BCI','Fairtrade','SA8000','WRAP','BSCI','LEED'];
const QUAL_CERTS = ['ISO 9001','ISO 14001','SEDEX','SGS Approved','Intertek Approved','Bureau Veritas'];
const SETTLEMENT_METHODS = ['Debit Note','Replacement of Goods','Discount on Next Order','Cash Settlement','Mutual Waiver'];

export default function SupplierModule({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['DASHBOARD','DIRECTORY','REGISTRATION','AUDIT','CAP','RATING','WARNINGS / BLACKLIST','CLAIMS','PRODUCTION'];
  const [tab, setTab] = useState('DASHBOARD');
  const [capSeed, setCapSeed] = useState(null);
  const [profileSup, setProfileSup] = useState(null);
  const [editSup, setEditSup] = useState(null);

  const suppliers = db.suppliers || [];
  const supplierNames = suppliers.map(s=>s.name);
  const canEdit = hasRight(currentUser, 'SUPPLIER', 'edit');

  // ═══════════════ DASHBOARD ═══════════════
  const Dashboard = () => {
    const audits = db.audits || [], caps = db.caps || [];
    const stats = {
      total: suppliers.length,
      garment: suppliers.filter(s=>s.supplierType==='Garment Supplier').length,
      rm: suppliers.filter(s=>s.supplierType==='Raw Material Supplier').length,
      va: suppliers.filter(s=>s.supplierType==='VA Process Supplier').length,
      approved: suppliers.filter(s=>s.approvalStatus==='Approved').length,
      pending: suppliers.filter(s=>(s.approvalStatus||'Pending Approval')==='Pending Approval').length,
      blacklisted: (db.blacklist||[]).filter(b=>b.status==='Blacklisted').length,
    };
    const topAudited = [...audits].sort((a,b)=>b.totalScore-a.totalScore).slice(0,8);
    return (
      <div>
        <Grid cols={4} mb={16}>
          <StatCard label="Total Suppliers" value={stats.total}
            sub={`${stats.garment}G · ${stats.rm}RM · ${stats.va}VA`} color={T.primary} onClick={()=>setTab('DIRECTORY')} />
          <StatCard label="Approved" value={stats.approved} sub={`${stats.pending} pending approval`} color={T.success} />
          <StatCard label="Audits Done" value={audits.length}
            sub={`${audits.filter(a=>a.capRequired).length} need CAP`} color={T.teal} onClick={()=>setTab('AUDIT')} />
          <StatCard label="Open CAPs" value={caps.filter(c=>c.status!=='Closed').length} color={T.warning} onClick={()=>setTab('CAP')} />
          <StatCard label="Warning Letters" value={(db.warningLetters||[]).length} color={T.orange} />
          <StatCard label="Blacklisted" value={stats.blacklisted} color={T.danger} />
          <StatCard label="Open Disputes" value={(db.claims||[]).filter(c=>!['Settled','Closed'].includes(c.status)).length} color={T.purple} onClick={()=>setTab('CLAIMS')} />
          <StatCard label="Rated Suppliers" value={(db.supplierRatings||[]).length} color={T.pink} onClick={()=>setTab('RATING')} />
        </Grid>
        <Grid cols={2} gap={16}>
          <Section title="Audit Score Ranking">
            {topAudited.length===0 && <span style={{ fontSize:12, color:T.textSub }}>No audits recorded yet.</span>}
            {topAudited.map(a=>(
              <MiniBar key={a.id} label={`${a.supplierName} (${a.auditDate})`} value={a.totalScore} max={100}
                extra={`${a.totalScore}% · Grade ${a.grade}`}
                color={a.grade==='A'?T.success:a.grade==='B'?T.warning:T.danger} />
            ))}
          </Section>
          <Section title="Suppliers Awaiting Approval">
            <DataTable columns={['Supplier','Type','Country','Registered']}
              rows={suppliers.filter(s=>(s.approvalStatus||'Pending Approval')==='Pending Approval')}
              renderCell={(r,c)=>({ 'Supplier':r.name, 'Type':r.supplierType,
                'Country':r.address?.country||'—', 'Registered':r.createdOn }[c])}
              actions={r => isManager(currentUser) && (
                <span style={{ display:'flex', gap:5 }}>
                  <Btn small color={T.success} label="Approve" onClick={()=>approveSupplier(r,'Approved')} />
                  <Btn small color={T.danger} label="Reject" onClick={()=>approveSupplier(r,'Rejected')} />
                </span>)}
              emptyMsg="All suppliers approved." />
          </Section>
        </Grid>
      </div>
    );
  };

  const approveSupplier = async (s, decision) => {
    if (!isManager(currentUser)) return alert('Manager rights required.');
    const upd = { ...s, approvalStatus:decision, status: decision==='Approved'?'Active':'Rejected',
      approvedBy:currentUser.name, approvedOn:today() };
    setDb(p=>({ ...p, suppliers: suppliers.map(x=>x.id===s.id?upd:x) }));
    await saveToDB('supplier', upd);
  };

  // ═══════════════ DIRECTORY + PROFILE ═══════════════
  const Directory = () => {
    const [filter, setFilter] = useState('All');
    const [q, setQ] = useState('');
    const cats = ['All', ...(db.supplierCategories||[]).map(c=>c.name||c).filter(Boolean)];
    const shown = suppliers.filter(s =>
      (filter==='All' || s.category===filter || s.supplierType===filter) &&
      (!q || s.name.toLowerCase().includes(q.toLowerCase())));
    const ratingOf = s => (db.supplierRatings||[]).find(r=>r.supplier===s.name);
    const isBlack = s => (db.blacklist||[]).some(b=>b.supplier===s.name && b.status==='Blacklisted');

    return (
      <Section title={`Supplier Directory (${shown.length})`}
        actions={<span style={{ display:'flex', gap:8 }}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search..."
            style={{ padding:'5px 8px', border:`1px solid ${T.border}`, borderRadius:5, fontSize:11 }} />
          <select value={filter} onChange={e=>setFilter(e.target.value)} style={{ fontSize:11, padding:4 }}>
            {[...cats,'Garment Supplier','Raw Material Supplier','VA Process Supplier'].map(c=><option key={c}>{c}</option>)}
          </select>
        </span>}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14 }}>
          {shown.map(s => {
            const r = ratingOf(s); const black = isBlack(s);
            const warns = (db.warningLetters||[]).filter(w=>w.supplier===s.name).length;
            const lastAudit = (db.audits||[]).filter(a=>a.supplierName===s.name).slice(-1)[0];
            const openCaps = (db.caps||[]).filter(c=>c.supplier===s.name && c.status!=='Closed').length;
            return (
              <div key={s.id} onClick={()=>setProfileSup(s)}
                style={{ background:'white', border:`1px solid ${black?T.danger:T.border}`, borderRadius:10,
                  padding:15, boxShadow:T.cardShadow, cursor:'pointer', opacity:black?0.8:1 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    {s.images?.companyLogo
                      ? <img src={s.images.companyLogo} alt="" style={{ width:36, height:36, borderRadius:6, objectFit:'cover' }} onError={e=>e.target.style.display='none'} />
                      : <div style={{ width:36, height:36, borderRadius:6, background:T.primary, color:'white',
                          display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:14 }}>
                          {s.name.slice(0,2).toUpperCase()}</div>}
                    <div>
                      <div style={{ fontWeight:800, fontSize:13.5 }}>{s.name}</div>
                      <div style={{ fontSize:10.5, color:T.textSub }}>{s.supplierType}</div>
                    </div>
                  </div>
                  {black ? <Badge status="Blacklisted"/> : <Badge status={s.approvalStatus||'Pending Approval'}/>}
                </div>
                <div style={{ margin:'8px 0 4px' }}>
                  <RatingStars value={r?.stars||0} readOnly />
                  {r?.badge && <Tag label={r.badge} color={T.warning} />}
                </div>
                <div style={{ fontSize:11, color:T.textSub, lineHeight:1.6 }}>
                  📍 {s.factoryAddress?.city || s.address?.city || '—'}{s.address?.country?`, ${s.address.country}`:''}<br/>
                  👤 {s.contact?.person||'—'} · 📞 {s.contact?.phone||s.contact?.mobile||'—'}<br/>
                  🏭 {s.totalLines||0} lines · Capacity {s.finishing?.capacity||'—'}/day
                </div>
                <div style={{ display:'flex', gap:6, marginTop:8, flexWrap:'wrap' }}>
                  {lastAudit && <Tag label={`Audit ${lastAudit.totalScore}% (${lastAudit.grade})`}
                    color={lastAudit.grade==='A'?T.success:lastAudit.grade==='B'?T.warning:T.danger} />}
                  {openCaps>0 && <Tag label={`${openCaps} open CAP`} color={T.purple} />}
                  {warns>0 && <Tag label={`${warns} warning`} color={T.danger} />}
                </div>
              </div>
            );
          })}
          {shown.length===0 && <div style={{ fontSize:12, color:T.textSub }}>No suppliers match. Register one in the REGISTRATION tab.</div>}
        </div>
      </Section>
    );
  };

  const SupplierProfile = ({ s }) => {
    const audits = (db.audits||[]).filter(a=>a.supplierName===s.name);
    const caps   = (db.caps||[]).filter(c=>c.supplier===s.name);
    const insp   = (db.inspections||[]).filter(i=>i.supplier===s.name);
    const done   = insp.filter(i=>['Passed','Failed'].includes(i.result));
    const rating = (db.supplierRatings||[]).find(r=>r.supplier===s.name);
    const passRate = done.length ? (done.filter(i=>i.result==='Passed').length/done.length*100).toFixed(1) : '—';
    return (
      <div>
        <Section title={`${s.name} — Supplier Profile`}
          actions={<span style={{ display:'flex', gap:6 }}>
            {canEdit && <Btn small outline label="✎ Edit" onClick={()=>{ setEditSup(s); setProfileSup(null); setTab('REGISTRATION'); }} />}
            <Btn small color="#64748b" label="← Directory" onClick={()=>setProfileSup(null)} /></span>}>
          <Grid cols={5} mb={12}>
            <StatCard label="Approval" value={s.approvalStatus||'Pending'} color={T.primary} />
            <StatCard label="Latest Audit" value={audits.length?`${audits.slice(-1)[0].totalScore}%`:'—'}
              sub={audits.length?`Grade ${audits.slice(-1)[0].grade}`:'no audit'} color={T.teal} />
            <StatCard label="Open CAPs" value={caps.filter(c=>c.status!=='Closed').length} color={T.warning} />
            <StatCard label="Inspections" value={done.length} sub={`${passRate}% pass`} color={T.success} />
            <StatCard label="Rating" value={rating?`${rating.stars}★`:'—'} sub={rating?.badge||''} color={T.purple} />
          </Grid>
          <Grid cols={2} gap={16}>
            <div>
              <Collapse title="Identity & Location" defaultOpen>
                <table style={{ fontSize:12, width:'100%' }}><tbody>
                  {[['Company ID',s.companyId],['Tax ID',s.taxId],['Ownership',s.ownership],
                    ['Head Office',`${s.address?.city||''} ${s.address?.country||''}`],
                    ['Factory',`${s.factoryAddress?.city||''} ${s.factoryAddress?.country||''}`],
                    ['Region',s.region],['Payment Terms',s.paymentTerms],['Lead Time',s.leadTime]].map(([k,v])=>(
                    <tr key={k}><td style={{ color:T.textSub, padding:'3px 8px 3px 0', width:120 }}>{k}</td><td>{v||'—'}</td></tr>
                  ))}
                </tbody></table>
                <MapEmbed lat={s.factoryAddress?.lat} lng={s.factoryAddress?.lng} label="Factory" height="150px" />
              </Collapse>
              <Collapse title="Contacts">
                <table style={{ fontSize:12, width:'100%' }}><tbody>
                  {Object.entries(s.contact||{}).map(([k,v])=>(
                    <tr key={k}><td style={{ color:T.textSub, padding:'3px 8px 3px 0', textTransform:'capitalize', width:120 }}>{k}</td><td>{v||'—'}</td></tr>
                  ))}
                </tbody></table>
                {(s.qaContacts||[]).length>0 && <>
                  <b style={{ fontSize:11 }}>Factory Quality Contacts</b>
                  <DataTable columns={['Name','Designation','Email','Phone']} rows={s.qaContacts}
                    renderCell={(r,c)=>({ Name:r.name, Designation:r.designation, Email:r.email, Phone:r.phone }[c])} />
                </>}
              </Collapse>
              <Collapse title="Quality Setup">
                <table style={{ fontSize:12, width:'100%' }}><tbody>
                  {Object.entries(s.qualitySetup||{}).map(([k,v])=>(
                    <tr key={k}><td style={{ color:T.textSub, padding:'3px 8px 3px 0', width:180 }}>
                      {k.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase())}</td><td>{v||'—'}</td></tr>
                  ))}
                </tbody></table>
              </Collapse>
            </div>
            <div>
              <Collapse title="Capacity" defaultOpen>
                <div style={{ fontSize:12 }}>
                  Sewing area <b>{s.totalSewingArea||'—'}</b> · Lines <b>{s.totalLines||0}</b> ·
                  Machines <b>{(s.sewingLines||[]).reduce((a,l)=>a+num(l.machines),0)}</b>
                </div>
                <DataTable columns={['Line #','Prod Cat','Machines','QC Stations','Eff %']}
                  rows={(s.sewingLines||[]).map((l,i)=>({ ...l, id:i }))}
                  renderCell={(r,c)=>({ 'Line #':r.id_||r.id+1, 'Prod Cat':r.category, 'Machines':r.machines,
                    'QC Stations':r.qc, 'Eff %':r.eff }[c])} emptyMsg="No line data." />
              </Collapse>
              <Collapse title="Evaluation & Certifications" defaultOpen>
                <div style={{ fontSize:12, marginBottom:6 }}>
                  Sustainability <RatingStars value={s.evaluation?.sustScore||0} readOnly /> ·
                  Quality <RatingStars value={s.evaluation?.qualityScore||0} readOnly />
                </div>
                <div>{(s.evaluation?.certifications||[]).map(c=><Tag key={c} label={c} color={T.success} />)}</div>
                <div>{(s.evaluation?.qualityCerts||[]).map(c=><Tag key={c} label={c} color={T.primary} />)}</div>
              </Collapse>
              <Collapse title="Audit History">
                <DataTable columns={['Date','Type','Score','Grade','Status']} rows={audits.slice().reverse()}
                  renderCell={(r,c)=>({ Date:r.auditDate, Type:r.auditType, Score:`${r.totalScore}%`,
                    Grade:r.grade, Status:r.status }[c])} emptyMsg="No audits." />
              </Collapse>
              <Collapse title="Warning & Blacklist History">
                <DataTable columns={['Date','Letter #','Reason']} rows={(db.warningLetters||[]).filter(w=>w.supplier===s.name)}
                  renderCell={(r,c)=>({ Date:r.date, 'Letter #':r.letterNo, Reason:r.reason }[c])} emptyMsg="No warnings." />
              </Collapse>
            </div>
          </Grid>
        </Section>
      </div>
    );
  };

  // ═══════════════ REGISTRATION ═══════════════
  const Registration = () => {
    const [f, setF] = useState(editSup ? { ...INIT_SUP, ...editSup } : { ...INIT_SUP });
    const set = (k,v) => setF(p=>({ ...p, [k]:v }));
    const setN = (grp,k,v) => setF(p=>({ ...p, [grp]:{ ...p[grp], [k]:v } }));
    const toggleCert = (key,val) => {
      const arr = f.evaluation[key]||[];
      setN('evaluation', key, arr.includes(val) ? arr.filter(c=>c!==val) : [...arr,val]);
    };
    const generateLines = (count) => {
      const n = parseInt(count,10)||0;
      set('sewingLines', Array.from({length:n},(_,i)=>({ id:i+1, category:'', machines:'', iron:'', qc:'', helpers:'', eff:'' })));
    };
    const applyLineToAll = () => {
      const first = f.sewingLines[0]; if (!first) return;
      set('sewingLines', f.sewingLines.map((l,i)=> i===0 ? l : { ...first, id:l.id }));
    };

    const save = async () => {
      if (!f.name) return alert('Company name is required');
      if (!f.supplierType) return alert('Supplier type is required');
      const entry = { ...f, id: f.id||uid(), createdOn: f.createdOn||today(),
        category: f.category || (f.supplierType==='Garment Supplier'?'Garment':f.rmType||f.vaType),
        registeredBy: currentUser.name };
      setDb(p=>({ ...p, suppliers:[...suppliers.filter(x=>x.id!==entry.id), entry] }));
      await saveToDB('supplier', entry);
      alert(`Supplier saved. ${entry.approvalStatus==='Approved'?'':'Awaiting manager approval before POs can be allocated.'}`);
      setEditSup(null); setF({ ...INIT_SUP }); setTab('DIRECTORY');
    };

    const totalMachines = (f.sewingLines||[]).reduce((a,l)=>a+num(l.machines),0);
    const smvMap = {}; (f.productCats||[]).forEach(pc=>{ if(pc.category) smvMap[pc.category]=num(pc.smv)||20; });
    const estCapacity = Math.round((f.sewingLines||[]).reduce((acc,l)=>
      acc + (num(l.machines)*480*(num(l.eff)/100)/(smvMap[(l.category||'').trim()]||20)), 0));

    return (
      <div>
        <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap', alignItems:'center' }}>
          {['Garment Supplier','Raw Material Supplier','VA Process Supplier'].map(t=>(
            <button key={t} onClick={()=>set('supplierType',t)}
              style={{ padding:'10px 20px', borderRadius:6, border:`1px solid ${T.border}`, cursor:'pointer', fontWeight:800, fontSize:12,
                background: f.supplierType===t?'#1f1f1f':'white', color: f.supplierType===t?'white':T.textSub }}>{t}</button>
          ))}
          {suppliers.length>0 && (
            <select onChange={e=>{ const s=suppliers.find(x=>String(x.id)===e.target.value); if(s) setF({ ...INIT_SUP, ...s }); }}
              style={{ marginLeft:'auto', padding:'8px 12px', border:`1px solid ${T.border}`, borderRadius:6, fontSize:12 }}>
              <option value="">— Edit Existing Supplier —</option>
              {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>

        <Section title="1. Identity & Location">
          <Grid cols={3}>
            <Field label="Company Name *" value={f.name} onChange={e=>set('name',e.target.value)} />
            <Field label="Company ID (CIN)" value={f.companyId} onChange={e=>set('companyId',e.target.value)} />
            <Field label="Tax ID (GST / VAT)" value={f.taxId} onChange={e=>set('taxId',e.target.value)} />
          </Grid>
          <Grid cols={4}>
            <Field label="Head Office — City" value={f.address.city} onChange={e=>setN('address','city',e.target.value)} />
            <Field label="State" value={f.address.state} onChange={e=>setN('address','state',e.target.value)} />
            <Field label="Country" value={f.address.country} onChange={e=>setN('address','country',e.target.value)}
              options={(db.countries||[]).map(c=>c.name||c)} />
            <Field label="ZIP" value={f.address.zip} onChange={e=>setN('address','zip',e.target.value)} />
          </Grid>
          <Grid cols={4}>
            <Field label="Factory — City" value={f.factoryAddress.city} onChange={e=>setN('factoryAddress','city',e.target.value)}
              options={(db.cities||[]).map(c=>c.name||c)} />
            <Field label="Factory Country" value={f.factoryAddress.country} onChange={e=>setN('factoryAddress','country',e.target.value)}
              options={(db.countries||[]).map(c=>c.name||c)} />
            <Field label="Factory Lat" value={f.factoryAddress.lat} onChange={e=>setN('factoryAddress','lat',e.target.value)} placeholder="26.9124" />
            <Field label="Factory Lng" value={f.factoryAddress.lng} onChange={e=>setN('factoryAddress','lng',e.target.value)} placeholder="75.7873" />
          </Grid>
          <Grid cols={3}>
            <Field label="Region (for dashboard filters)" value={f.region} onChange={e=>set('region',e.target.value)}
              options={(db.regions||[]).map(r=>r.name||r)} />
            <Field label="Ownership Type" value={f.ownership} onChange={e=>set('ownership',e.target.value)}
              options={['Sole Proprietorship','Partnership','Company (Board)','Other']} />
            <Field label="Supplier Category" value={f.category} onChange={e=>set('category',e.target.value)}
              options={(db.supplierCategories||[]).map(c=>c.name||c).filter(Boolean)} />
          </Grid>
          <MapEmbed lat={f.factoryAddress.lat} lng={f.factoryAddress.lng} label="Factory location" />
        </Section>

        <Section title="2. Contact Information">
          <Grid cols={4}>
            <Field label="Contact Person" value={f.contact.person} onChange={e=>setN('contact','person',e.target.value)} />
            <Field label="Phone" value={f.contact.phone} onChange={e=>setN('contact','phone',e.target.value)} />
            <Field label="Mobile / WhatsApp" value={f.contact.mobile}
              onChange={e=>setF(p=>({ ...p, contact:{ ...p.contact, mobile:e.target.value, whatsapp:e.target.value } }))} />
            <Field label="Email" value={f.contact.email} onChange={e=>setN('contact','email',e.target.value)} />
            <Field label="Website" value={f.contact.website} onChange={e=>setN('contact','website',e.target.value)} />
            <Field label="LinkedIn" value={f.contact.linkedin} onChange={e=>setN('contact','linkedin',e.target.value)} />
            <Field label="Instagram" value={f.contact.instagram} onChange={e=>setN('contact','instagram',e.target.value)} />
          </Grid>
          <b style={{ fontSize:11, color:T.textSub }}>Factory Quality Contacts (used for CAP, claims & escalation mails)</b>
          <DynamicTable columns={[{label:'Name',key:'name'},{label:'Designation',key:'designation'},
            {label:'Email',key:'email'},{label:'Phone',key:'phone'}]}
            data={f.qaContacts} setData={v=>set('qaContacts',v)} onAdd={()=>set('qaContacts',[...f.qaContacts,{}])} dense />
        </Section>

        <Section title="3. Management & Clients">
          <Grid cols={2} gap={20}>
            <div>
              <b style={{ fontSize:11, color:T.textSub }}>Owners / Directors</b>
              <DynamicTable columns={[{label:'Name',key:'name'},{label:'Designation',key:'des'}]}
                data={f.owners} setData={v=>set('owners',v)} onAdd={()=>set('owners',[...f.owners,{}])} dense />
            </div>
            <div>
              <b style={{ fontSize:11, color:T.textSub }}>Factories / Production Units</b>
              <DynamicTable columns={[{label:'Factory Name',key:'name'},{label:'Address',key:'addr'}]}
                data={f.factories} setData={v=>set('factories',v)} onAdd={()=>set('factories',[...f.factories,{}])} dense />
            </div>
          </Grid>
          <b style={{ fontSize:11, color:T.textSub }}>Customers & Markets</b>
          <DynamicTable columns={[{label:'Customer Name',key:'name'},{label:'Share %',key:'share'},{label:'Market',key:'market'}]}
            data={f.customers} setData={v=>set('customers',v)} onAdd={()=>set('customers',[...f.customers,{}])} dense />
        </Section>

        <Section title="4. Company & Factory Media">
          <Grid cols={2}>
            <div>
              <Field label="Company Logo (URL)" value={f.images.companyLogo} onChange={e=>setN('images','companyLogo',e.target.value)} placeholder="https://..." />
              {f.images.companyLogo && <img src={f.images.companyLogo} alt="logo" style={{ height:48, borderRadius:4, border:`1px solid ${T.border}` }} onError={e=>e.target.style.display='none'} />}
            </div>
            <Field label="Company Profile PDF (URL)" value={f.files.profile} onChange={e=>setN('files','profile',e.target.value)} />
            <Field label="Company Images (comma-separated URLs)" value={(f.images.companyImages||[]).join(', ')}
              onChange={e=>setN('images','companyImages',e.target.value.split(',').map(u=>u.trim()).filter(Boolean))} />
            <Field label="Factory Images (comma-separated URLs)" value={(f.images.factoryImages||[]).join(', ')}
              onChange={e=>setN('images','factoryImages',e.target.value.split(',').map(u=>u.trim()).filter(Boolean))} />
          </Grid>
        </Section>

        {/* ── GARMENT SPECIFIC ── */}
        {f.supplierType==='Garment Supplier' && <>
          <Section title="5. Product Categories & Capacity">
            <DynamicTable columns={[{label:'Category',key:'category'},{label:'Share %',key:'share'},{label:'Avg SMV',key:'smv'}]}
              data={f.productCats} setData={v=>set('productCats',v)} onAdd={()=>set('productCats',[...f.productCats,{}])} dense />
          </Section>
          <Section title="6. Factory Capacity — Line Wise">
            <div style={{ display:'flex', gap:15, marginBottom:14, flexWrap:'wrap', alignItems:'flex-end' }}>
              <Field label="Total Sewing Area (sq ft)" value={f.totalSewingArea} onChange={e=>set('totalSewingArea',e.target.value)} />
              <Field label="Total Lines" type="number" value={f.totalLines} onChange={e=>set('totalLines',e.target.value)} style={{ width:110 }} />
              <div style={{ paddingBottom:10 }}><Btn small label="Generate Lines" onClick={()=>generateLines(f.totalLines)} /></div>
            </div>
            <DynamicTable columns={[{label:'Line #',key:'id'},{label:'Prod Cat',key:'category'},{label:'Machines',key:'machines'},
              {label:'Iron Tables',key:'iron'},{label:'QC Stations',key:'qc'},{label:'Helpers',key:'helpers'},{label:'Eff %',key:'eff'}]}
              data={f.sewingLines} setData={v=>set('sewingLines',v)} onAdd={()=>set('sewingLines',[...f.sewingLines,{}])} dense />
            {f.sewingLines.length>0 && <>
              <button onClick={applyLineToAll} style={{ fontSize:11, color:T.primary, border:'none', background:'none',
                cursor:'pointer', textDecoration:'underline', marginBottom:8 }}>Apply Line 1 config to all lines</button>
              <div style={{ padding:10, background:'#f0fdf4', borderRadius:6, display:'flex', gap:20, fontSize:12, flexWrap:'wrap' }}>
                <span>Total Machines: <b>{totalMachines}</b></span>
                <span>Est. Capacity: <b style={{ color:T.success }}>{estCapacity.toLocaleString()} pcs/day</b></span>
              </div>
            </>}
          </Section>
          <Section title="7. Cutting & Finishing">
            <Grid cols={2} gap={20}>
              <div>
                <h5 style={{ margin:'0 0 8px', fontSize:13 }}>Cutting</h5>
                <Field label="Total Area" value={f.cutting.area} onChange={e=>setN('cutting','area',e.target.value)} />
                <Field label="Total Tables" value={f.cutting.tables} onChange={e=>setN('cutting','tables',e.target.value)} />
                <Field label="Table Length" value={f.cutting.length} onChange={e=>setN('cutting','length',e.target.value)} />
                <Field label="Type" value={f.cutting.type} onChange={e=>setN('cutting','type',e.target.value)} options={['Manual','Auto Spreader','Auto Cutter']} />
              </div>
              <div>
                <h5 style={{ margin:'0 0 8px', fontSize:13 }}>Finishing</h5>
                <Field label="Total Area" value={f.finishing.area} onChange={e=>setN('finishing','area',e.target.value)} />
                <Field label="Total Tables" value={f.finishing.tables} onChange={e=>setN('finishing','tables',e.target.value)} />
                <Field label="Packers" value={f.finishing.packers} onChange={e=>setN('finishing','packers',e.target.value)} />
                <Field label="Daily Capacity (pcs)" value={f.finishing.capacity} onChange={e=>setN('finishing','capacity',e.target.value)} />
              </div>
            </Grid>
          </Section>
        </>}

        {/* ── RAW MATERIAL ── */}
        {f.supplierType==='Raw Material Supplier' && (
          <Section title="5. Product Categories">
            <div style={{ marginBottom:14, display:'flex', gap:20 }}>
              {['Fabric','Trims'].map(t=>(
                <label key={t} style={{ fontSize:13, cursor:'pointer' }}>
                  <input type="radio" checked={f.rmType===t} onChange={()=>set('rmType',t)} style={{ marginRight:5 }} />
                  {t==='Trims'?'Trims / Accessories':t}
                </label>
              ))}
            </div>
            <DynamicTable
              columns={f.rmType==='Fabric'
                ? [{label:'Category',key:'category'},{label:'Weave/Knit',key:'weave'},{label:'Content',key:'content'},
                   {label:'GSM',key:'weight'},{label:'Width (in)',key:'width'},{label:'Price Range',key:'price'}]
                : [{label:'Item Type',key:'category'},{label:'Design',key:'design'},{label:'Size Range',key:'size'},
                   {label:'Material',key:'material'},{label:'Price Range',key:'price'}]}
              data={f.productCats} setData={v=>set('productCats',v)} onAdd={()=>set('productCats',[...f.productCats,{}])} dense />
          </Section>
        )}

        {/* ── VA PROCESS ── */}
        {f.supplierType==='VA Process Supplier' && (
          <Section title="5. Process Capabilities">
            <Field label="Service Type" value={f.vaType} onChange={e=>set('vaType',e.target.value)}
              options={['Print','Embroidery','Washing','Other']} />
            <Grid cols={3}>
              <Field label="Technique" value={f.vaDetails.technique} onChange={e=>setN('vaDetails','technique',e.target.value)} />
              <Field label="Mode" value={f.vaDetails.mode} onChange={e=>setN('vaDetails','mode',e.target.value)} options={['Manual','Auto','Semi-Auto']} />
              <Field label="Lead Time (days)" value={f.vaDetails.leadTime} onChange={e=>setN('vaDetails','leadTime',e.target.value)} />
            </Grid>
            {f.vaType==='Print' && <Field label="Total Tables / Machines" value={f.vaDetails.tablesOrMachines} onChange={e=>setN('vaDetails','tablesOrMachines',e.target.value)} />}
            {f.vaType==='Embroidery' && <Grid cols={3}>
              <Field label="Handworkers" value={f.vaDetails.handworkers} onChange={e=>setN('vaDetails','handworkers',e.target.value)} />
              <Field label="Umbrella Machines" value={f.vaDetails.umbrellaMachines} onChange={e=>setN('vaDetails','umbrellaMachines',e.target.value)} />
              <Field label="Industrial Machines" value={f.vaDetails.industrialMachines} onChange={e=>setN('vaDetails','industrialMachines',e.target.value)} />
            </Grid>}
            {f.vaType==='Washing' && <Grid cols={4}>
              <Field label="Wash Machines" value={f.vaDetails.washingMachines} onChange={e=>setN('vaDetails','washingMachines',e.target.value)} />
              <Field label="Dryers" value={f.vaDetails.dryingMachines} onChange={e=>setN('vaDetails','dryingMachines',e.target.value)} />
              <Field label="PP Stations" value={f.vaDetails.ppStations} onChange={e=>setN('vaDetails','ppStations',e.target.value)} />
              <Field label="Laser Machines" value={f.vaDetails.laserMachines} onChange={e=>setN('vaDetails','laserMachines',e.target.value)} />
            </Grid>}
            <Grid cols={2}>
              <Field label="Sample Capacity / Day" value={f.vaDetails.sampleCap} onChange={e=>setN('vaDetails','sampleCap',e.target.value)} />
              <Field label="Production Capacity / Day" value={f.vaDetails.prodCap} onChange={e=>setN('vaDetails','prodCap',e.target.value)} />
            </Grid>
          </Section>
        )}

        <Section title="8. Quality Setup (drives audit readiness & QA planning)">
          <Grid cols={4}>
            <Field label="Total QC Headcount" type="number" value={f.qualitySetup.qcHeadcount} onChange={e=>setN('qualitySetup','qcHeadcount',e.target.value)} />
            <Field label="Inline QC per Line" type="number" value={f.qualitySetup.inlineQcPerLine} onChange={e=>setN('qualitySetup','inlineQcPerLine',e.target.value)} />
            <Field label="End-line QC" type="number" value={f.qualitySetup.endlineQc} onChange={e=>setN('qualitySetup','endlineQc',e.target.value)} />
            <Field label="QA Independent Reporting" value={f.qualitySetup.qaIndependent} onChange={e=>setN('qualitySetup','qaIndependent',e.target.value)} options={['Yes','No']} />
            <Field label="On-Site Lab" value={f.qualitySetup.labOnSite} onChange={e=>setN('qualitySetup','labOnSite',e.target.value)} options={['Yes','No']} />
            <Field label="Metal Detector" value={f.qualitySetup.metalDetector} onChange={e=>setN('qualitySetup','metalDetector',e.target.value)} options={['Yes','No']} />
            <Field label="Pull Tester" value={f.qualitySetup.pullTester} onChange={e=>setN('qualitySetup','pullTester',e.target.value)} options={['Yes','No']} />
            <Field label="Needle Control Policy" value={f.qualitySetup.needlePolicy} onChange={e=>setN('qualitySetup','needlePolicy',e.target.value)} options={['Yes','No']} />
            <Field label="4-Point Fabric System" value={f.qualitySetup.fourPointSystem} onChange={e=>setN('qualitySetup','fourPointSystem',e.target.value)} options={['Yes','No']} />
            <Field label="Traffic Light System" value={f.qualitySetup.trafficLight} onChange={e=>setN('qualitySetup','trafficLight',e.target.value)} options={['Yes','No']} />
            <Field label="Default AQL" value={f.qualitySetup.aqlStandard} onChange={e=>setN('qualitySetup','aqlStandard',e.target.value)} options={['1.0','1.5','2.5','4.0']} />
            <Field label="Measurement Audit Frequency" value={f.qualitySetup.measurementAuditFreq} onChange={e=>setN('qualitySetup','measurementAuditFreq',e.target.value)} />
          </Grid>
        </Section>

        <Section title="9. Evaluation Criteria">
          <Grid cols={2} gap={20}>
            <div style={{ background:'#f0fdf4', padding:15, borderRadius:8, border:'1px solid #bbf7d0' }}>
              <b style={{ color:'#15803d', fontSize:13 }}>🌱 SUSTAINABILITY</b>
              <div style={{ margin:'8px 0' }}><RatingStars value={f.evaluation.sustScore||0} onChange={v=>setN('evaluation','sustScore',v)} /></div>
              <Grid cols={2}>
                <Field label="Water Usage (L/unit)" value={f.evaluation.waterUsage} onChange={e=>setN('evaluation','waterUsage',e.target.value)} />
                <Field label="GHG (kg CO₂/unit)" value={f.evaluation.ghgEmission} onChange={e=>setN('evaluation','ghgEmission',e.target.value)} />
              </Grid>
              <Field label="Waste Management" value={f.evaluation.wasteMgmt} onChange={e=>setN('evaluation','wasteMgmt',e.target.value)}
                options={['Basic','Moderate','Advanced','Zero Waste']} />
              <Field label="Social Audit Score (%)" value={f.evaluation.socialAudit} onChange={e=>setN('evaluation','socialAudit',e.target.value)} />
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
                {SUST_CERTS.map(c=>(
                  <span key={c} onClick={()=>toggleCert('certifications',c)}
                    style={{ padding:'3px 8px', borderRadius:12, fontSize:10, cursor:'pointer', fontWeight:600,
                      background:(f.evaluation.certifications||[]).includes(c)?T.success:'#e2e8f0',
                      color:(f.evaluation.certifications||[]).includes(c)?'white':T.textSub }}>{c}</span>
                ))}
              </div>
            </div>
            <div style={{ background:'#eff6ff', padding:15, borderRadius:8, border:'1px solid #bfdbfe' }}>
              <b style={{ color:'#1d4ed8', fontSize:13 }}>🛡 QUALITY</b>
              <div style={{ margin:'8px 0' }}><RatingStars value={f.evaluation.qualityScore||0} onChange={v=>setN('evaluation','qualityScore',v)} /></div>
              <Grid cols={2}>
                <Field label="Rejection Rate (%)" value={f.evaluation.rejectionRate} onChange={e=>setN('evaluation','rejectionRate',e.target.value)} />
                <Field label="Last Audit Score (%)" value={f.evaluation.lastAuditScore} onChange={e=>setN('evaluation','lastAuditScore',e.target.value)} />
              </Grid>
              <Field label="Last Audit Date" type="date" value={f.evaluation.auditDate} onChange={e=>setN('evaluation','auditDate',e.target.value)} />
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:8 }}>
                {QUAL_CERTS.map(c=>(
                  <span key={c} onClick={()=>toggleCert('qualityCerts',c)}
                    style={{ padding:'3px 8px', borderRadius:12, fontSize:10, cursor:'pointer', fontWeight:600,
                      background:(f.evaluation.qualityCerts||[]).includes(c)?T.primary:'#e2e8f0',
                      color:(f.evaluation.qualityCerts||[]).includes(c)?'white':T.textSub }}>{c}</span>
                ))}
              </div>
            </div>
          </Grid>
        </Section>

        <Section title="10. Capacity Calendar & Holidays">
          <div style={{ fontSize:11, color:T.textSub, marginBottom:10, background:'#fffbeb', padding:'8px 12px',
            borderRadius:6, border:'1px solid #fde68a' }}>
            Enter only <b>public holidays</b> per month. Working days = total days − Sundays − public holidays.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10 }}>
            {f.calendar.map((m,i)=>{
              const net = Math.max(0, MONTH_DAYS[i] - SUNDAYS[i] - (parseInt(m.holidays,10)||0));
              return (
                <div key={i} style={{ border:`1px solid ${T.border}`, padding:10, borderRadius:6, background:'#f8fafc' }}>
                  <div style={{ fontWeight:700, fontSize:12, marginBottom:6, display:'flex', justifyContent:'space-between' }}>
                    {MONTHS_SHORT[i]}<span style={{ fontSize:10, color:T.textSub }}>{MONTH_DAYS[i]}d</span>
                  </div>
                  <input type="number" min="0" max="15" value={m.holidays}
                    onChange={e=>set('calendar', f.calendar.map((x,ix)=> ix===i
                      ? { ...x, holidays:e.target.value, workingDays: Math.max(0, MONTH_DAYS[i]-SUNDAYS[i]-(parseInt(e.target.value,10)||0)) } : x))}
                    style={{ width:'100%', padding:5, border:'1px solid #cbd5e1', borderRadius:4, fontSize:11, boxSizing:'border-box' }} />
                  <div style={{ fontSize:10, color:T.success, fontWeight:700, marginTop:4 }}>{net} working days</div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section title="11. Commercial Terms">
          <Grid cols={3}>
            <Field label="Agreed Wastage %" type="number" value={f.agreedWastage} onChange={e=>set('agreedWastage',e.target.value)} />
            <Field label="Payment Terms" value={f.paymentTerms} onChange={e=>set('paymentTerms',e.target.value)} />
            <Field label="Standard Lead Time (days)" value={f.leadTime} onChange={e=>set('leadTime',e.target.value)} />
          </Grid>
        </Section>

        <button onClick={save} style={{ width:'100%', padding:15, background:T.success, color:'white', border:'none',
          borderRadius:8, fontWeight:'bold', fontSize:15, cursor:'pointer', marginTop:10, letterSpacing:'0.05em' }}>
          ✓ SAVE SUPPLIER PROFILE
        </button>
      </div>
    );
  };

  // ═══════════════ RATING (Super Admin only) ═══════════════
  const Rating = () => {
    const canRate = isSuperAdmin(currentUser);
    const [f, setF] = useState({ supplier:'', stars:0, badge:'', remarks:'',
      scores:{ quality:0, delivery:0, responsiveness:0, compliance:0, costCompetitiveness:0 } });
    const ratings = db.supplierRatings || [];

    // Auto-computed quality performance for reference while rating
    const perf = (name) => {
      const insp = (db.inspections||[]).filter(i=>i.supplier===name && ['Passed','Failed'].includes(i.result));
      const audits = (db.audits||[]).filter(a=>a.supplierName===name);
      const caps = (db.caps||[]).filter(c=>c.supplier===name);
      return {
        passRate: insp.length ? (insp.filter(i=>i.result==='Passed').length/insp.length*100).toFixed(1) : '—',
        auditAvg: audits.length ? (audits.reduce((a,x)=>a+x.totalScore,0)/audits.length).toFixed(1) : '—',
        capClosure: caps.length ? (caps.filter(c=>c.status==='Closed').length/caps.length*100).toFixed(0) : '—',
        inspections: insp.length,
      };
    };
    const p = f.supplier ? perf(f.supplier) : null;
    const avgScore = Object.values(f.scores).reduce((a,b)=>a+b,0)/5;

    const save = async () => {
      if (!canRate) return alert('Supplier rating & star badging is reserved for Super Admin.');
      if (!f.supplier || !f.stars) return alert('Select supplier and give a star rating');
      const entry = { id: uid(), ...f, computedAvg:+avgScore.toFixed(2), ratedBy:currentUser.name, date:today() };
      setDb(p2=>({ ...p2, supplierRatings:[...ratings.filter(r=>r.supplier!==f.supplier), entry] }));
      await saveToDB('supplierRating', entry);
      setF({ supplier:'', stars:0, badge:'', remarks:'', scores:{ quality:0, delivery:0, responsiveness:0, compliance:0, costCompetitiveness:0 } });
    };

    return (
      <div>
        <Section title={`Supplier Performance Rating & Star Badging ${canRate?'':'— view only (Super Admin right required)'}`}>
          {canRate && <>
            <Grid cols={3}>
              <Field label="Supplier" value={f.supplier} onChange={e=>setF({...f,supplier:e.target.value})} options={supplierNames} />
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:11, fontWeight:700, display:'block', marginBottom:4, color:T.textSub }}>Overall Star Rating</label>
                <RatingStars value={f.stars} onChange={v=>setF({...f,stars:v})} />
              </div>
              <Field label="Badge" value={f.badge} onChange={e=>setF({...f,badge:e.target.value})}
                options={['Gold Partner','Silver Partner','Bronze Partner','Preferred','Probation','Under Review']} />
            </Grid>
            {p && (
              <div style={{ background:'#f8fafc', border:`1px solid ${T.border}`, borderRadius:8, padding:12, marginBottom:12, fontSize:12 }}>
                <b>Actual performance for {f.supplier}:</b> inspection pass rate <b>{p.passRate}%</b> ({p.inspections} inspections) ·
                audit average <b>{p.auditAvg}%</b> · CAP closure <b>{p.capClosure}%</b>
              </div>
            )}
            <b style={{ fontSize:11, color:T.textSub }}>Scorecard (0–10 each)</b>
            <Grid cols={5}>
              {Object.keys(f.scores).map(k=>(
                <Field key={k} type="number" label={k.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase())}
                  value={f.scores[k]} onChange={e=>setF({...f,scores:{...f.scores,[k]:Math.min(10,Math.max(0,num(e.target.value)))}})} />
              ))}
            </Grid>
            <div style={{ fontSize:12.5, fontWeight:700, marginBottom:10 }}>Computed average: <b style={{ color:T.primary }}>{avgScore.toFixed(2)}/10</b></div>
            <Field label="Remarks" type="textarea" value={f.remarks} onChange={e=>setF({...f,remarks:e.target.value})} />
            <Btn label="Save Rating & Badge" color={T.success} onClick={save} />
          </>}
        </Section>
        <Section title="Rating Register">
          <DataTable columns={['Supplier','Stars','Badge','Scorecard Avg','Remarks','Rated By','Date']} rows={ratings}
            renderCell={(r,c)=>({ Supplier:<b>{r.supplier}</b>, Stars:<RatingStars value={r.stars} readOnly />,
              Badge:r.badge && <Tag label={r.badge} color={T.warning}/>, 'Scorecard Avg':r.computedAvg??'—',
              Remarks:r.remarks, 'Rated By':r.ratedBy, Date:r.date }[c])} />
        </Section>
      </div>
    );
  };

  // ═══════════════ WARNINGS & BLACKLIST ═══════════════
  const Warnings = () => {
    const [w, setW] = useState({ supplier:'', reason:'', category:'Quality Failure', severity:'Warning', detail:'' });
    const letters = db.warningLetters || [];
    const blacklist = db.blacklist || [];
    const issue = async () => {
      if (!w.supplier || !w.reason) return alert('Supplier and reason are required');
      if (w.severity==='Blacklist') {
        if (!isManager(currentUser)) return alert('Blacklisting requires Manager/Admin rights.');
        const entry = { id:uid(), supplier:w.supplier, reason:w.reason, category:w.category, detail:w.detail,
          by:currentUser.name, date:today(), status:'Blacklisted' };
        setDb(p=>({ ...p, blacklist:[...blacklist, entry] }));
        await saveToDB('blacklist', entry);
      } else {
        const count = letters.filter(l=>l.supplier===w.supplier).length + 1;
        const entry = { id:uid(), supplier:w.supplier, reason:w.reason, category:w.category, detail:w.detail,
          letterNo:count, by:currentUser.name, date:today(), acknowledged:'No' };
        setDb(p=>({ ...p, warningLetters:[...letters, entry] }));
        await saveToDB('warningLetter', entry);
        if (count>=3) alert(`⚠ This is warning letter #${count} for ${w.supplier}. Blacklisting review is recommended.`);
      }
      setW({ supplier:'', reason:'', category:'Quality Failure', severity:'Warning', detail:'' });
    };
    const printLetter = (l) => printHTML(`Warning Letter ${l.letterNo}`, `
      <h1>TOQUM · Supplier Warning Letter</h1>
      <table><tr><td><b>To:</b> ${l.supplier}</td><td><b>Letter No:</b> ${l.letterNo}</td><td><b>Date:</b> ${l.date}</td></tr>
        <tr><td colspan="3"><b>Category:</b> ${l.category}</td></tr></table>
      <p><b>Subject:</b> ${l.reason}</p><p>${(l.detail||'').replace(/\n/g,'<br/>')}</p>
      <p>You are required to submit a corrective action plan within 7 working days. Repeated non-conformity
      may lead to suspension of new order allocation or blacklisting.</p>
      <table class="sign"><tr><th>Issued By</th><th>Quality Manager</th><th>Supplier Acknowledgement</th></tr>
        <tr><td>${l.by}</td><td></td><td></td></tr></table>`);
    const revoke = async (b) => {
      if (!isManager(currentUser)) return alert('Manager right required.');
      const upd = { ...b, status:'Revoked', revokedBy:currentUser.name, revokedOn:today() };
      setDb(p=>({ ...p, blacklist: blacklist.map(x=>x.id===b.id?upd:x) }));
      await saveToDB('blacklist', upd);
    };
    return (
      <div>
        <Section title="Issue Warning Letter / Blacklist">
          <Grid cols={4}>
            <Field label="Supplier" value={w.supplier} onChange={e=>setW({...w,supplier:e.target.value})} options={supplierNames} />
            <Field label="Category" value={w.category} onChange={e=>setW({...w,category:e.target.value})}
              options={['Quality Failure','Repeated Defects','CAP Non-Compliance','Late Delivery','Unauthorised Subcontracting','Safety Violation','Falsified Records','Other']} />
            <Field label="Reason / Subject" value={w.reason} onChange={e=>setW({...w,reason:e.target.value})} />
            <Field label="Action" value={w.severity} onChange={e=>setW({...w,severity:e.target.value})} options={['Warning','Blacklist']} />
          </Grid>
          <Field label="Detailed Statement" type="textarea" value={w.detail} onChange={e=>setW({...w,detail:e.target.value})} />
          <Btn label={w.severity==='Blacklist'?'Blacklist Supplier':'Issue Warning Letter'} color={T.danger} onClick={issue} />
        </Section>
        <Grid cols={2} gap={16}>
          <Section title="Warning Letter History">
            <DataTable columns={['Date','Supplier','Letter #','Category','Reason','By']} rows={letters.slice().reverse()}
              renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, 'Letter #':<b>{r.letterNo}</b>,
                Category:r.category, Reason:r.reason, By:r.by }[c])}
              actions={r => <Btn small outline label="🖨" onClick={()=>printLetter(r)} />} />
          </Section>
          <Section title="Blacklist History">
            <DataTable columns={['Date','Supplier','Category','Reason','Status','By']} rows={blacklist.slice().reverse()}
              renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, Category:r.category, Reason:r.reason,
                Status:<Badge status={r.status}/>, By:r.by }[c])}
              actions={r => r.status==='Blacklisted' && <Btn small outline label="Revoke" onClick={()=>revoke(r)} />} />
          </Section>
        </Grid>
      </div>
    );
  };

  // ═══════════════ CLAIMS ═══════════════
  const Claims = () => {
    const claims = db.claims || [];
    const [f, setF] = useState({ complainant:'', respondent:'', reason:'', description:'', lossAmount:'',
      poRef:'', proofs:[] });
    const [detail, setDetail] = useState(null);
    const qaContacts = (db.users||[]).filter(u=>['QA','Manager','Admin','Super Admin'].includes(u.role)).map(u=>u.name);

    const addProof = (e) => {
      const files = Array.from(e.target.files||[]);
      Promise.all(files.map(file => new Promise(res=>{
        const r = new FileReader();
        r.onload = () => res({ name:file.name, type:file.type.startsWith('video')?'video':'photo', data:r.result });
        r.readAsDataURL(file);
      }))).then(list => setF(p=>({ ...p, proofs:[...p.proofs, ...list] })));
      e.target.value='';
    };

    const raise = async () => {
      if (!f.complainant || !f.respondent || !f.reason) return alert('Complainant, respondent and reason are required');
      if (f.complainant===f.respondent) return alert('Complainant and respondent cannot be the same supplier');
      const entry = { id:uid(), ...f, lossAmount:num(f.lossAmount), raisedOn:today(), status:'Open',
        timeline:[{ date:today(), event:'Claim raised', by:f.complainant }], debitNote:null, settlement:null, escalation:null };
      setDb(p=>({ ...p, claims:[...claims, entry] }));
      await saveToDB('claim', entry);
      setF({ complainant:'', respondent:'', reason:'', description:'', lossAmount:'', poRef:'', proofs:[] });
      alert('Dispute raised.');
    };

    const update = async (claim, patch, event) => {
      const upd = { ...claim, ...patch, timeline:[...claim.timeline, { date:today(), event, by:currentUser.name }] };
      setDb(p=>({ ...p, claims: claims.map(c=>c.id===claim.id?upd:c) }));
      await saveToDB('claim', upd);
      setDetail(upd);
    };

    const ClaimDetail = ({ claim }) => {
      const [dn, setDn] = useState({ amount:claim.lossAmount, method:SETTLEMENT_METHODS[0] });
      const [esc, setEsc] = useState({ buyerContact:'', mails:[], statement:'' });
      const addMails = (e) => { setEsc(p=>({ ...p, mails:[...p.mails, ...Array.from(e.target.files||[]).map(x=>x.name)] })); e.target.value=''; };
      const graceEnd = claim.escalation ? addDays(claim.escalation.notifiedOn||claim.escalation.date, 3) : null;
      const graceOver = graceEnd && today() > graceEnd;
      return (
        <Section title={`Dispute #${String(claim.id).slice(-5)} — ${claim.complainant} vs ${claim.respondent}`}
          actions={<Btn small color="#64748b" label="← Back" onClick={()=>setDetail(null)} />}>
          <Grid cols={4} mb={10}>
            <StatCard label="Status" value={claim.status} color={T.warning} />
            <StatCard label="Claimed Loss" value={`$${num(claim.lossAmount).toLocaleString()}`} color={T.danger} />
            <StatCard label="Settled" value={claim.settlement?`$${num(claim.settlement.amount).toLocaleString()}`:'—'} color={T.success} />
            <StatCard label="Balance" value={`$${(num(claim.lossAmount)-num(claim.settlement?.amount)).toLocaleString()}`} color={T.purple} />
          </Grid>
          <p style={{ fontSize:12.5 }}><b>Reason:</b> {claim.reason} · <b>PO Ref:</b> {claim.poRef||'—'}<br/>
            <b>Details:</b> {claim.description||'—'}</p>
          <div style={{ marginBottom:10 }}>
            <b style={{ fontSize:12 }}>Proofs:</b>{' '}
            {claim.proofs?.length ? claim.proofs.map((p,i)=><Tag key={i} label={`${p.type==='video'?'🎬':'📷'} ${p.name}`} color={T.teal} />)
              : <span style={{ fontSize:12, color:T.textSub }}>none attached</span>}
          </div>
          {claim.proofs?.filter(p=>p.type==='photo').length>0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:8, marginBottom:12 }}>
              {claim.proofs.filter(p=>p.type==='photo').map((p,i)=>(
                <img key={i} src={p.data} alt={p.name} style={{ width:'100%', height:90, objectFit:'cover', borderRadius:6, border:`1px solid ${T.border}` }} />
              ))}
            </div>
          )}

          {['Open','Under Review'].includes(claim.status) && (
            <div style={{ border:`1px dashed ${T.border}`, borderRadius:8, padding:12, marginBottom:10 }}>
              <b style={{ fontSize:12 }}>Debit Note & Settlement</b>
              <Grid cols={4}>
                <Field label="Debit Note Amount" type="number" value={dn.amount} onChange={e=>setDn({...dn,amount:e.target.value})} />
                <Field label="Settlement Method" value={dn.method} onChange={e=>setDn({...dn,method:e.target.value})} options={SETTLEMENT_METHODS} />
                <div style={{ display:'flex', alignItems:'flex-end', gap:6, paddingBottom:10 }}>
                  <Btn small label="Generate Debit Note"
                    onClick={()=>update(claim,{ debitNote:{ no:`DN-${String(claim.id).slice(-5)}`, amount:num(dn.amount), method:dn.method, date:today() }, status:'Debit Note' },'Debit note generated')} />
                  <Btn small color={T.success} label="Mark Settled"
                    onClick={()=>update(claim,{ settlement:{ amount:num(dn.amount), method:dn.method, date:today() }, status:'Settled' },`Settled via ${dn.method}`)} />
                </div>
              </Grid>
            </div>
          )}

          {!claim.escalation && !['Settled','Closed'].includes(claim.status) && (
            <div style={{ border:`1px dashed ${T.warning}`, borderRadius:8, padding:12, marginBottom:10 }}>
              <b style={{ fontSize:12 }}>Escalate to Buyer — requires Quality-team contact + minimum 5 mails + detailed statement</b>
              <Grid cols={3}>
                <Field label="Buyer Quality Contact" value={esc.buyerContact} onChange={e=>setEsc({...esc,buyerContact:e.target.value})} options={qaContacts} />
                <div style={{ marginBottom:10 }}>
                  <label style={{ fontSize:11, fontWeight:700, display:'block', marginBottom:4, color:T.textSub }}>
                    Attach Mail Evidence ({esc.mails.length}/5 min)</label>
                  <input type="file" multiple accept=".eml,.msg,.pdf,.png,.jpg" onChange={addMails} style={{ fontSize:11 }} />
                </div>
                <Field label="Detailed Version of Dispute" type="textarea" value={esc.statement} onChange={e=>setEsc({...esc,statement:e.target.value})} />
              </Grid>
              <Btn small color={T.warning} label="Escalate to Buyer QA" onClick={()=>{
                if (!esc.buyerContact) return alert('Choose the buyer Quality contact person.');
                if (esc.mails.length < 5) return alert('At least 5 mails are required to prove legitimacy of the claim.');
                if (!esc.statement) return alert('A detailed statement of the dispute is required.');
                if (!(claim.proofs||[]).length) return alert('Photo or video proof must be attached before escalation.');
                update(claim, { escalation:{ ...esc, date:today(), notifiedOn:today() }, status:'Grace Period' },
                  `Escalated to ${esc.buyerContact}; ${claim.respondent} notified — 3-day grace period started`);
                alert(`Escalation submitted.\n${esc.buyerContact} will mail ${claim.respondent} directly to inform and provide a chance.\n${claim.respondent} has 3 days to sit with ${claim.complainant} before the complaint is officially recorded.`);
              }} />
            </div>
          )}

          {claim.status==='Grace Period' && (
            <div style={{ background:'#fdab3d18', border:`1px solid ${T.warning}`, borderRadius:8, padding:10, fontSize:12, marginBottom:10 }}>
              ⏳ Grace period: {claim.respondent} has until <b>{graceEnd}</b> to resolve with {claim.complainant}.
              {graceOver
                ? <span> Grace period is over. <Btn small color={T.danger} label="Record Officially & Start Monitoring"
                    onClick={()=>update(claim,{ status:'Escalated', officiallyRecorded:today() },'Complaint officially recorded — supplier monitoring started')} /></span>
                : <span> Buyer contact <b>{claim.escalation?.buyerContact}</b> has informed the respondent.</span>}
              {' '}<Btn small outline color={T.success} label="Resolved in Grace"
                onClick={()=>update(claim,{ status:'Settled', settlement:{ amount:0, method:'Mutual Waiver', date:today() } },'Resolved during grace period')} />
            </div>
          )}
          {claim.status==='Escalated' && (
            <Btn small color={T.purple} label="Move to Arbitration" onClick={()=>update(claim,{ status:'Arbitration' },'Moved to arbitration')} />
          )}

          <div style={{ marginTop:12 }}>
            <b style={{ fontSize:12 }}>Timeline</b>
            {claim.timeline.map((t,i)=>(
              <div key={i} style={{ fontSize:11.5, padding:'3px 0 3px 10px', borderLeft:`3px solid ${T.primary}`, marginTop:4 }}>
                <b>{t.date}</b> — {t.event} <span style={{ color:T.textSub }}>({t.by})</span>
              </div>
            ))}
          </div>
        </Section>
      );
    };

    const summary = supplierNames.map(s=>{
      const asComp = claims.filter(c=>c.complainant===s);
      const asResp = claims.filter(c=>c.respondent===s);
      const loss = asResp.reduce((a,c)=>a+num(c.lossAmount),0);
      const settled = asResp.reduce((a,c)=>a+num(c.settlement?.amount),0);
      const reasons = [...new Set(asResp.map(c=>c.reason))].join(', ');
      return { id:s, supplier:s, raised:asComp.length, against:asResp.length, reasons, loss, settled, balance:loss-settled };
    }).filter(r=>r.raised||r.against);

    if (detail) return <ClaimDetail claim={claims.find(c=>c.id===detail.id)||detail} />;
    return (
      <div>
        <Section title="Raise Supplier-to-Supplier Dispute">
          <Grid cols={5}>
            <Field label="Complainant Supplier" value={f.complainant} onChange={e=>setF({...f,complainant:e.target.value})} options={supplierNames} />
            <Field label="Against (Respondent)" value={f.respondent} onChange={e=>setF({...f,respondent:e.target.value})} options={supplierNames} />
            <Field label="Reason" value={f.reason} onChange={e=>setF({...f,reason:e.target.value})}
              options={['Late Delivery','Quality Failure','Short Shipment','Wrong Specification','Shade Variation','Shrinkage Issue','Damage in Transit','Non-performance','Other']} />
            <Field label="PO Reference" value={f.poRef} onChange={e=>setF({...f,poRef:e.target.value})} options={(db.pos||[]).map(p=>p.poNo)} />
            <Field label="Claimed Loss (USD)" type="number" value={f.lossAmount} onChange={e=>setF({...f,lossAmount:e.target.value})} />
          </Grid>
          <Field label="Detailed Description" type="textarea" value={f.description} onChange={e=>setF({...f,description:e.target.value})} />
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <label style={{ fontSize:12, fontWeight:700, color:T.primary, cursor:'pointer' }}>
              📎 Attach Photos / Videos ({f.proofs.length})
              <input type="file" multiple accept="image/*,video/*" onChange={addProof} style={{ display:'none' }} />
            </label>
            <Btn label="Raise Dispute" color={T.danger} onClick={raise} />
          </div>
        </Section>
        <Section title="Dispute Register">
          <DataTable columns={['Raised','Complainant','Respondent','Reason','PO','Loss','Status']} rows={claims.slice().reverse()}
            renderCell={(r,c)=>({ Raised:r.raisedOn, Complainant:r.complainant, Respondent:<b>{r.respondent}</b>,
              Reason:r.reason, PO:r.poRef||'—', Loss:`$${num(r.lossAmount).toLocaleString()}`, Status:<Badge status={r.status}/> }[c])}
            actions={r => <Btn small outline label="Open" onClick={()=>setDetail(r)} />} />
        </Section>
        <Section title="Supplier-wise Dispute Summary">
          <DataTable columns={['Supplier','Raised','Against','Reasons','Total Loss','Settled','Balance to Settle']} rows={summary}
            renderCell={(r,c)=>({ Supplier:<b>{r.supplier}</b>, Raised:r.raised, Against:r.against, Reasons:r.reasons||'—',
              'Total Loss':`$${r.loss.toLocaleString()}`, Settled:`$${r.settled.toLocaleString()}`,
              'Balance to Settle':<b style={{ color:r.balance>0?T.danger:T.success }}>${r.balance.toLocaleString()}</b> }[c])} />
        </Section>
      </div>
    );
  };

  // ═══════════════ ROOT ═══════════════
  return (
    <Page title="SUPPLIER MANAGEMENT" subtitle="Registration · audit · CAP · rating · warnings · claims">
      <TabBar tabs={TABS} active={tab} onChange={t=>{ setTab(t); setProfileSup(null); }} />
      {tab==='DASHBOARD'   && <Dashboard />}
      {tab==='DIRECTORY'   && (profileSup ? <SupplierProfile s={profileSup} /> : <Directory />)}
      {tab==='REGISTRATION'&& <Registration />}
      {tab==='AUDIT'       && <AuditTab db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser}
                                suppliers={suppliers} onCreateCAP={a=>{ setCapSeed(a); setTab('CAP'); }} />}
      {tab==='CAP'         && <CapTab db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser}
                                suppliers={suppliers} seedAudit={capSeed} clearSeed={()=>setCapSeed(null)} />}
      {tab==='RATING'      && <Rating />}
      {tab==='WARNINGS / BLACKLIST' && <Warnings />}
      {tab==='CLAIMS'      && <Claims />}
      {tab==='PRODUCTION'  && <ProductionEntry db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser} />}
    </Page>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PRODUCTION ENTRY — Supplier logs daily output, QA reviews/rejects
// ═══════════════════════════════════════════════════════════════════
function ProductionEntry({ db, setDb, saveToDB, currentUser }) {
  const isQA = isManager(currentUser) || (currentUser.role||'').toLowerCase().includes('qa');
  const allPos = db.pos || [];
  const myPos  = isQA ? allPos : allPos.filter(p =>
    p.supplier === currentUser.name || p.supplier === currentUser.supplierName ||
    (db.qaAllocations||[]).some(a => a.supplier === p.supplier && a.qaMember === currentUser.name)
  );

  const [selPO, setSelPO] = useState('');
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [filterPO, setFilterPO] = useState('');

  const notify = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };

  const po      = allPos.find(p => p.poNo === selPO);
  const colors  = [...new Set((po?.matrix||[]).map(m=>m.color).filter(Boolean))];
  const sizes   = [...new Set((po?.matrix||[]).map(m=>m.size).filter(Boolean))];
  const allLogs = db.productionLogs || [];
  const logs    = filterPO ? allLogs.filter(l=>l.poNo===filterPO) : allLogs;

  const todayStr     = today();
  const yesterdayStr = new Date(Date.now()-86400000).toISOString().split('T')[0];
  const STAGES = ['Cutting','Sewing','Washing','Finishing','Packing'];

  const isLocked = (log) => log.status !== 'Rejected' && log.date < yesterdayStr;

  const addLine = () => setLines(p=>[...p,{stage:'Packing',line:'',
    color:colors[0]||'',size:sizes[0]||'',qty:'',date:todayStr}]);

  const updLine = (i,k,v) => setLines(p=>p.map((x,xi)=>xi===i?{...x,[k]:v}:x));

  const submit = async () => {
    if (!selPO) return alert('Select a PO first');
    const valid = lines.filter(l=>parseInt(l.qty)>0);
    const bad   = valid.filter(l=>l.date!==todayStr&&l.date!==yesterdayStr);
    if (bad.length) return alert(`Only today (${todayStr}) or yesterday (${yesterdayStr}) allowed.`);
    if (!valid.length) return alert('Enter at least one line with qty > 0');
    setSaving(true);
    const newLogs = valid.map(l=>({
      id:uid(), poNo:selPO, styleNo:po?.styleNo||'', supplier:po?.supplier||'',
      stage:l.stage, line:l.line, color:l.color, size:l.size,
      qty:parseInt(l.qty)||0, date:l.date,
      enteredBy:currentUser.name, enteredAt:new Date().toISOString(),
      status:'Submitted', history:[],
    }));
    setDb(p=>({...p,productionLogs:[...allLogs,...newLogs]}));
    for (const e of newLogs) await saveToDB('productionLog', e);
    setLines([]); setSaving(false);
    notify(`${newLogs.length} line(s) saved for PO ${selPO}`);
  };

  const rejectEntry = async (log, reason) => {
    const upd = {...log, status:'Rejected', rejectedBy:currentUser.name,
      rejectedAt:new Date().toISOString(), rejectionReason:reason,
      history:[...(log.history||[]),{action:'Rejected',by:currentUser.name,
        at:new Date().toISOString(),note:reason}]};
    setDb(p=>({...p,productionLogs:allLogs.map(x=>x.id===upd.id?upd:x)}));
    await saveToDB('productionLog', upd);
    setRejectModal(null);
    notify('Entry rejected — supplier can now correct it','warn');
  };

  const approveEntry = async (log) => {
    const upd = {...log, status:'Approved', approvedBy:currentUser.name,
      approvedAt:new Date().toISOString(),
      history:[...(log.history||[]),{action:'Approved',by:currentUser.name,at:new Date().toISOString()}]};
    setDb(p=>({...p,productionLogs:allLogs.map(x=>x.id===upd.id?upd:x)}));
    await saveToDB('productionLog', upd);
    notify('Entry approved');
  };

  return (
    <Page title="DAILY PRODUCTION" subtitle="Supplier enters output · QA approves or rejects">
      {!isQA && (
        <Section title="Enter Production — Today / Yesterday Only"
          actions={<span style={{fontSize:11,color:T.textSub}}>
            Allowed dates: {yesterdayStr} and {todayStr}
          </span>}>
          <div style={{background:'#fff8e6',border:'1px solid #fde68a',borderRadius:6,
            padding:'8px 12px',marginBottom:12,fontSize:11.5,color:'#8a5a00'}}>
            🔒 You can only enter for <b>today and yesterday</b>.
            Older entries are locked. Ask your QA to reject an entry if correction is needed.
          </div>
          <Field label="Select PO" value={selPO}
            onChange={e=>{setSelPO(e.target.value);setLines([]);}}
            options={myPos.map(p=>p.poNo)}/>
          {po && <div style={{fontSize:11.5,color:T.textSub,margin:'4px 0 10px'}}>
            {po.styleNo} · {po.supplier} · {po.brand} · {num(po.qty).toLocaleString()} pcs
          </div>}
          {selPO && <>
            <div style={{overflowX:'auto'}}>
              <table style={{borderCollapse:'collapse',fontSize:11.5,width:'100%'}}>
                <thead><tr style={{background:'#f1f5f9'}}>
                  {['Date','Stage','Line','Color','Size','Qty',''].map(h=>(
                    <th key={h} style={{padding:'6px 8px',textAlign:'left',fontWeight:800,
                      borderBottom:`1px solid ${T.border}`,fontSize:11}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>{lines.map((l,i)=>(
                  <tr key={i} style={{borderBottom:`1px solid ${T.border}`}}>
                    <td style={{padding:'4px 6px'}}>
                      <select value={l.date} onChange={e=>updLine(i,'date',e.target.value)}
                        style={{padding:'5px',border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}>
                        <option value={todayStr}>Today ({todayStr})</option>
                        <option value={yesterdayStr}>Yesterday ({yesterdayStr})</option>
                      </select>
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      <select value={l.stage} onChange={e=>updLine(i,'stage',e.target.value)}
                        style={{padding:'5px',border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}>
                        {STAGES.map(s=><option key={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      <input value={l.line} onChange={e=>updLine(i,'line',e.target.value)}
                        placeholder="L-01" style={{width:52,padding:'5px',
                          border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}/>
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      {colors.length
                        ? <select value={l.color} onChange={e=>updLine(i,'color',e.target.value)}
                            style={{padding:'5px',border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}>
                            {colors.map(c=><option key={c}>{c}</option>)}
                          </select>
                        : <input value={l.color} onChange={e=>updLine(i,'color',e.target.value)}
                            placeholder="Color" style={{width:80,padding:'5px',
                              border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}/>}
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      {sizes.length
                        ? <select value={l.size} onChange={e=>updLine(i,'size',e.target.value)}
                            style={{padding:'5px',border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}>
                            {sizes.map(s=><option key={s}>{s}</option>)}
                          </select>
                        : <input value={l.size} onChange={e=>updLine(i,'size',e.target.value)}
                            placeholder="Size" style={{width:60,padding:'5px',
                              border:`1px solid ${T.border}`,borderRadius:4,fontSize:11}}/>}
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      <input type="number" value={l.qty} onChange={e=>updLine(i,'qty',e.target.value)}
                        placeholder="0" style={{width:80,padding:'5px',border:`1px solid ${T.border}`,
                          borderRadius:4,fontSize:11,fontWeight:700}}/>
                    </td>
                    <td style={{padding:'4px 6px'}}>
                      <button onClick={()=>setLines(p=>p.filter((_,xi)=>xi!==i))}
                        style={{background:'none',border:'none',color:T.danger,cursor:'pointer',fontSize:16}}>×</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{display:'flex',gap:10,marginTop:10,flexWrap:'wrap'}}>
              <Btn small outline label="+ Add Line" onClick={addLine} color={T.primary}/>
              <Btn label={saving?'Saving…':'Submit Production'} color={T.success}
                disabled={saving||!lines.length} onClick={submit}/>
            </div>
          </>}
        </Section>
      )}

      <Section title="Production Log"
        actions={
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <span style={{fontSize:11,color:T.textSub}}>Filter by PO:</span>
            <select value={filterPO} onChange={e=>setFilterPO(e.target.value)}
              style={{padding:'5px 8px',border:`1px solid ${T.border}`,borderRadius:6,fontSize:12}}>
              <option value="">All POs</option>
              {[...new Set(allLogs.map(l=>l.poNo))].map(p=><option key={p}>{p}</option>)}
            </select>
          </div>
        }>
        {logs.length===0
          ? <div style={{fontSize:12,color:T.textSub,padding:8}}>No production entries yet.</div>
          : <div style={{overflowX:'auto'}}>
            <table style={{borderCollapse:'collapse',fontSize:11.5,width:'100%'}}>
              <thead><tr style={{background:'linear-gradient(90deg,#1f1f1f,#2d3748)',color:'white'}}>
                {['Date','PO','Stage','Color','Size','Qty','Entered By','Status',...(isQA?['Approve/Reject']:[]),'History'].map(h=>(
                  <th key={h} style={{padding:'7px 10px',textAlign:'left',fontWeight:700}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{[...logs].sort((a,b)=>b.date.localeCompare(a.date)).map(r=>{
                const locked = isLocked(r);
                return (
                  <tr key={r.id} style={{borderBottom:`1px solid ${T.border}`,
                    background:r.status==='Rejected'?'#fdeaec08':r.status==='Approved'?'#f0fdf408':'white'}}>
                    <td style={{padding:'7px 10px',fontWeight:r.date===todayStr?800:400,
                      color:locked&&r.status!=='Rejected'?T.textSub:'inherit'}}>{r.date}</td>
                    <td style={{padding:'7px 10px'}}>{r.poNo}</td>
                    <td style={{padding:'7px 10px'}}>{r.stage}</td>
                    <td style={{padding:'7px 10px'}}>{r.color||'—'}</td>
                    <td style={{padding:'7px 10px'}}>{r.size||'—'}</td>
                    <td style={{padding:'7px 10px',fontWeight:800}}>{num(r.qty).toLocaleString()}</td>
                    <td style={{padding:'7px 10px',fontSize:11,color:T.textSub}}>{r.enteredBy}</td>
                    <td style={{padding:'7px 10px'}}>
                      <span style={{padding:'2px 9px',borderRadius:10,fontSize:10.5,fontWeight:800,
                        background:r.status==='Approved'?'#e9f9f0':r.status==='Rejected'?'#fdeaec':'#fff8e6',
                        color:r.status==='Approved'?T.success:r.status==='Rejected'?T.danger:T.warning}}>
                        {locked&&r.status==='Submitted'?'Locked':r.status||'Submitted'}
                      </span>
                    </td>
                    {isQA && (
                      <td style={{padding:'7px 8px'}}>
                        <div style={{display:'flex',gap:5}}>
                          {r.status!=='Approved' && (
                            <Btn small color={T.success} label="✓" onClick={()=>approveEntry(r)}/>
                          )}
                          <Btn small color={T.danger} label={locked?'Unlock':'✕'}
                            onClick={()=>setRejectModal({log:r,reason:''})}/>
                        </div>
                      </td>
                    )}
                    <td style={{padding:'7px 8px'}}>
                      {(r.history||[]).length>0
                        ? <span style={{fontSize:10,color:T.textSub,cursor:'help'}}
                            title={(r.history||[]).map(h=>`${h.action} by ${h.by}: ${h.note||''}`).join('\n')}>
                            📋 {(r.history||[]).length}
                          </span>
                        : '—'}
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>}
        {!isQA && logs.some(l=>l.status==='Rejected') && (
          <div style={{marginTop:10,background:'#fdeaec',border:`1px solid ${T.danger}`,
            borderRadius:6,padding:'8px 12px',fontSize:11.5,color:T.danger}}>
            ⚠ You have rejected entries. Select the PO above and resubmit the corrected figures.
          </div>
        )}
      </Section>

      {rejectModal && (
        <Modal title="Reject / Unlock Entry" onClose={()=>setRejectModal(null)}
          actions={<>
            <Btn label="Send Rejection" color={T.danger}
              disabled={!rejectModal.reason.trim()}
              onClick={()=>rejectEntry(rejectModal.log,rejectModal.reason)}/>
            <Btn label="Cancel" color="#64748b" onClick={()=>setRejectModal(null)}/>
          </>}>
          <div style={{fontSize:12,marginBottom:10,color:T.textSub}}>
            {rejectModal.log.poNo} · {rejectModal.log.date} · {rejectModal.log.stage} ·
            {rejectModal.log.color} {rejectModal.log.size} · {num(rejectModal.log.qty).toLocaleString()} pcs
          </div>
          <Field label="Reason for rejection *" type="textarea"
            value={rejectModal.reason}
            onChange={e=>setRejectModal(p=>({...p,reason:e.target.value}))}
            placeholder="e.g. Packing qty for Black/L doesn't match daily report — please correct"/>
          <div style={{fontSize:11,color:T.textSub,marginTop:6}}>
            Supplier will be able to correct and resubmit. All changes tracked in history.
          </div>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </Page>
  );
}
